/** F1b provider-entity presentation rules, verified 26 September 2026.
 * These never mint programme write context or replace a recorded provider decision.
 * Missing effective evidence is review, including empty or unconfirmed categories.
 */
export type ComplianceSurfaceState = 'VISIBLE' | 'REVIEW' | 'NOT_APPLICABLE';
export interface ComplianceSurfaceFacts {
  revision: string;
  fact_revision: number;
  identity: string;
  confirmed: boolean;
  /** B1 facts reconciled by the existing B2 reader; absent on older deployments. */
  registration_complete?: boolean;
  registration_scope?: string;
  declared_programmes?: string[] | null;
  review_state?: 'READY' | 'REVIEW_REQUIRED';
  categories: string[] | null;
  government: boolean | null;
  specialist_programmes: string[] | null;
  specialist_only: boolean | null;
  deposits_ever_held?: boolean | null;
  contradictory_deposits?: boolean;
  retained_prudential?: boolean;
  reporting_start_month: number | null;
  approved_homes?: Array<{ facility_id: string; places: number; specialist: boolean | null }>;
  audit_scopes?: Array<'HOME_COMMUNITY' | 'RESIDENTIAL'>;
  facets: Array<'qi' | 'audit' | 'prudential'>;
  ndis_registered: boolean;
}
export interface ComplianceSurfaceVisibility extends ComplianceSurfaceFacts {
  qi?: ComplianceSurfaceState;
  prudential?: ComplianceSurfaceState;
  duties: Partial<
    Record<
      | 'fps2'
      | 'fps34'
      | 'deposit'
      | 'apcs'
      | 'acfr'
      | 'qfr'
      | 'complaints'
      | 'governingBody'
      | 'rn247',
      ComplianceSurfaceState
    >
  >;
}

export function resolveComplianceSurfaceVisibility(
  f: ComplianceSurfaceFacts,
): ComplianceSurfaceVisibility {
  const categories = f.categories ?? [];
  const known = f.confirmed && categories.length > 0;
  const cat6 = categories.includes('6');
  const cat456 = categories.some((c) => ['4', '5', '6'].includes(c));
  const programmes = f.specialist_programmes;
  const specialistsKnown = programmes !== null && f.specialist_only !== null;
  const excludedFinancial = programmes?.some((p) => ['CHSP', 'NATSIFACP', 'TCP'].includes(p));
  const mixedFinancial = excludedFinancial && f.specialist_only === false;
  const rule = (applies: boolean): ComplianceSurfaceState =>
    known ? (applies ? 'VISIBLE' : 'NOT_APPLICABLE') : 'REVIEW';
  const fps2 = !known || f.government === null ? 'REVIEW' : rule(cat456 && !f.government);
  const fps34 =
    !known || f.government === null || programmes === null
      ? 'REVIEW'
      : rule(cat6 && !f.government && !programmes.includes('NATSIFACP'));
  const deposit =
    !known || (cat6 && f.deposits_ever_held == null)
      ? 'REVIEW'
      : rule(cat6 && f.deposits_ever_held === true);
  const acfr =
    !known ||
    !specialistsKnown ||
    mixedFinancial ||
    programmes?.includes('TCP') ||
    f.reporting_start_month === null
      ? 'REVIEW'
      : rule(!(f.specialist_only && excludedFinancial));
  const qfr =
    !known ||
    !specialistsKnown ||
    mixedFinancial ||
    programmes?.includes('TCP') ||
    f.reporting_start_month === null
      ? 'REVIEW'
      : rule(
          !(
            f.specialist_only &&
            programmes.some((p) => p === 'CHSP' || (p === 'NATSIFACP' && !cat6))
          ),
        );
  const apcs = deposit !== 'VISIBLE' ? deposit : acfr === 'VISIBLE' ? 'VISIBLE' : 'REVIEW';
  const homes = f.approved_homes ?? [];
  const qi = !known
    ? 'REVIEW'
    : !cat6
      ? homes.some((h) => h.places > 0)
        ? 'REVIEW'
        : 'NOT_APPLICABLE'
      : homes.some((h) => h.places > 0 && h.specialist === false)
        ? 'VISIBLE'
        : 'REVIEW';
  // Historical records and positive deposits remain reachable even where current
  // registration differs. Visibility is deliberately broader than applicability.
  const prudential =
    f.retained_prudential ||
    f.deposits_ever_held ||
    [fps2, fps34, deposit, apcs, acfr, qfr].includes('VISIBLE')
      ? 'VISIBLE'
      : !known || [fps2, fps34, deposit, acfr, qfr].includes('REVIEW')
        ? 'REVIEW'
        : 'NOT_APPLICABLE';
  return {
    ...f,
    ...(f.facets.includes('qi') ? { qi } : {}),
    ...(f.facets.includes('prudential') ? { prudential } : {}),
    duties: {
      ...(f.facets.includes('prudential') ? { fps2, fps34, deposit, apcs, acfr, qfr } : {}),
      ...(f.facets.includes('qi')
        ? {
            complaints: rule(cat456),
            governingBody: !specialistsKnown
              ? 'REVIEW'
              : rule(
                  categories.some((c) => ['5', '6'].includes(c)) &&
                    !(
                      f.specialist_only && programmes.some((p) => ['MPS', 'NATSIFACP'].includes(p))
                    ),
                ),
            rn247: !specialistsKnown ? 'REVIEW' : rule(cat6 && !f.specialist_only),
          }
        : {}),
    },
  };
}

/** A provider-wide mainstream home never confirms another home's service scope. */
export function resolveQiHomeVisibility(
  facts: ComplianceSurfaceFacts | null | undefined,
  facilityId: string | null | undefined,
): ComplianceSurfaceState {
  const home = facts?.approved_homes?.find((h) => h.facility_id === facilityId);
  if (!facts?.confirmed || !facts.categories?.includes('6') || !home) return 'REVIEW';
  if (home.specialist === true) return 'NOT_APPLICABLE';
  return home.places > 0 && home.specialist === false ? 'VISIBLE' : 'REVIEW';
}
